import { useNavigator } from '@repo/toolkit/use-navigator'
import ActionButton from '@repo/ui/action-button'
import { Column } from '@repo/ui/data-table'
import { DuplicateIcon, PlusIcon } from '@repo/ui/icons'
import InnerBox from '@repo/ui/inner-box'
import Modal from '@repo/ui/modal'
import { useMutation } from '@tanstack/react-query'
import React, { useState } from 'react'
import { toast } from 'sonner'

import { API, constructApiUrl } from '../../../../api/apis.config'
import { duplicateOlTeflPrice } from '../../../../api/ols/tefl-prices/duplicate-ol-tefl-price'
import { Paths } from '../../../../lib/constants/path'
import { useFilters } from '../../../../lib/hooks/use-filters'
import { useModalManager } from '../../../../lib/hooks/use-modal-manager'
import useTranslate from '../../../../lib/hooks/use-translate'
import {
  OlTeflPrice,
  OlTeflPriceFilters
} from '../../../../lib/types/models/ol/tefl-price/ol-tefl-price'
import { PeriodProperties } from '../../../../lib/types/models/period/period.properties'
import {
  transformOlTeflPriceFiltersToUrl,
  transformUrlToOlTeflPriceFilters
} from '../../../../lib/utils/filters-mapping/ol-tefl-price'
import { showAxiosError } from '../../../../lib/utils/functions/misc.functions'
import { getTeflPriceColumns } from '../../../../views/ol-personal-space/fees/utils'
import { constructOlPath } from '../../../../views/ols/views/utils'
import FilterDrawer from '../../../elements/drawers/filter-drawer'
import DynamicDataTable, { DynamicTableRef } from '../../../elements/dynamic-table'
import { countFilters } from '../../../elements/dynamic-table/utils/countFilters'
import withPeriods, { WithPeriodsProps } from '../../../hoc/with-periods'
import { OlTeflPricesFilter } from './ol-tefl-prices-filter'

type Props = WithPeriodsProps & {
  olId: number
}

const OlTeflPrices: React.FC<Props> = ({ periods, olId }) => {
  const tableRef = React.useRef<DynamicTableRef<OlTeflPrice>>(null)
  const [isTableLoading, setIsTableLoading] = useState(false)
  const { navigateWithEvent, navigate } = useNavigator()
  const { t } = useTranslate()

  const { modalState, selectedData, toggleModal, closeModel } = useModalManager<
    'filters' | 'duplicate',
    OlTeflPrice
  >(['filters', 'duplicate'])

  const { selectedFilter, filter, onFilterChange, resetFilters, onSelectedFilterChange } =
    useFilters<OlTeflPriceFilters>({
      initialFilters: {
        formulas: [],
        periods: PeriodProperties.getCurrentYearPeriods(periods ?? []).map((period) => period.name)
      },
      options: { allowSearchParam: true },
      transformFilterToUrl: transformOlTeflPriceFiltersToUrl,
      transformUrlToFilter: transformUrlToOlTeflPriceFilters
    })

  const duplicateOlTeflPriceMutation = useMutation({
    mutationFn: duplicateOlTeflPrice,
    onError: showAxiosError,
    onSuccess: (duplicatedOlTeflPrice) => {
      toast.success('Prix TEFL dupliqué avec succès')
      navigate(
        constructOlPath(
          Paths.OL_DETAILS_UPDATE_TEFL_PRICES_PAGE.replace(
            ':teflPriceId',
            duplicatedOlTeflPrice.id.toString()
          ),
          olId
        )
      )
    }
  })

  const teflPriceColumns: Column<OlTeflPrice>[] = [
    ...getTeflPriceColumns('TEFL', t),
    {
      accessorKey: 'id',
      header: '',
      cell: ({ row }) => (
        <ActionButton
          variant="primary"
          icon={DuplicateIcon}
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            toggleModal('duplicate', row.original)
          }}
        />
      )
    }
  ]

  return (
    <InnerBox className="rounded-2xl p-0">
      <DynamicDataTable<OlTeflPrice, OlTeflPriceFilters>
        ref={tableRef}
        onLoadingChange={setIsTableLoading}
        title={'Prix TEFL'}
        deleteMessage={'Êtes-vous sûr de vouloir supprimer ce prix TEFL ?'}
        columns={teflPriceColumns}
        selectable
        isSearchable={false}
        allowSearchParam
        onRowClick={(olTeflPrice, event) => {
          navigateWithEvent(
            constructOlPath(
              Paths.OL_DETAILS_UPDATE_TEFL_PRICES_PAGE.replace(
                ':teflPriceId',
                olTeflPrice.id.toString()
              ),
              olId
            ),
            event
          )
        }}
        endpoint={constructApiUrl(API.ols.getOlTeflPrices.url, { olId })}
        filters={selectedFilter}
        filtersLength={countFilters(selectedFilter)}
        version="FULL"
        onFiltersClick={() => toggleModal('filters')}
        actions={[
          {
            type: 'global',
            icon: PlusIcon,
            label: 'Ajouter un nouveau prix par jour',
            fn: () => {
              navigate(constructOlPath(Paths.OL_DETAILS_CREATE_TEFL_PRICES_PAGE, olId))
            }
          }
        ]}
      />

      <FilterDrawer
        isOpen={modalState.filters}
        selectedFilter={selectedFilter}
        isLoading={isTableLoading}
        onClose={() => closeModel('filters')}
        onReset={resetFilters}
        onApply={() => onSelectedFilterChange({ ...filter })}
        onFilterChange={onFilterChange}
      >
        <OlTeflPricesFilter filter={filter} onFilterChange={onFilterChange} />
      </FilterDrawer>

      {selectedData && (
        <Modal
          variant="confirmation"
          icon={DuplicateIcon}
          isOpen={modalState.duplicate}
          onCancel={() => closeModel('duplicate')}
          title="Êtes-vous sûr de vouloir dupliquer ce prix TEFL ?"
          isLoading={duplicateOlTeflPriceMutation.isPending}
          actionButtonsOptions={{
            confirmText: 'Dupliquer',
            cancelText: 'Annuler',
            onConfirm: () => duplicateOlTeflPriceMutation.mutate({ olId, id: selectedData.id })
          }}
        />
      )}
    </InnerBox>
  )
}

export default withPeriods(OlTeflPrices)
